/**
 * OrderJourneyBar — horizontal delivery milestone centrepiece (Sprint 8.6).
 * Uses getMilestoneView for tracker-parity language.
 */

import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { getMilestoneView } from "../services/orderMilestoneView.js";
import { getCorridorLabel } from "../services/orderMissionSummary.js";

const stateColor = (state, theme) => {
  if (state === "completed") return theme.palette.success.main;
  if (state === "current") return theme.palette.primary.main;
  if (state === "problem") return theme.palette.error.main;
  return theme.palette.grey[400];
};

/**
 * @param {{
 *   order: Object,
 *   trip?: Object|null,
 *   estimatedDelivery?: string|Date|null,
 * }} props
 */
export default function OrderJourneyBar({
  order,
  trip = null,
  estimatedDelivery = null,
}) {
  const view = useMemo(() => getMilestoneView(order, trip), [order, trip]);
  const corridor = useMemo(() => getCorridorLabel(order), [order]);
  const eta = estimatedDelivery || order?.estimatedDelivery || null;

  const updatedLabel = useMemo(() => {
    if (!view.lastUpdatedAt) return null;
    try {
      return formatDistanceToNow(new Date(view.lastUpdatedAt), {
        addSuffix: true,
      });
    } catch {
      return null;
    }
  }, [view.lastUpdatedAt]);

  const stages = view.stages || [];

  return (
    <Card sx={{ mb: 3 }} data-testid="order-journey-bar">
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
          Journey
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0,
            overflowX: "auto",
            pb: 1,
          }}
        >
          {stages.map((stage, idx) => {
            const isLast = idx === stages.length - 1;
            return (
              <Box
                key={stage.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: stages.length > 6 ? 88 : 100,
                  flex: "1 1 0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={(theme) => ({
                      width: stage.state === "current" ? 14 : 12,
                      height: stage.state === "current" ? 14 : 12,
                      borderRadius: "50%",
                      bgcolor:
                        stage.state === "upcoming"
                          ? "transparent"
                          : stateColor(stage.state, theme),
                      border: `2px solid ${stateColor(stage.state, theme)}`,
                      zIndex: 1,
                    })}
                    data-testid={
                      stage.state === "current"
                        ? "journey-stage-current"
                        : undefined
                    }
                    data-stage-key={stage.key}
                    data-stage-state={stage.state}
                  />
                  {!isLast ? (
                    <Box
                      sx={(theme) => ({
                        position: "absolute",
                        top: 6,
                        left: "50%",
                        width: "100%",
                        height: 2,
                        bgcolor:
                          stage.state === "completed"
                            ? theme.palette.success.main
                            : theme.palette.grey[300],
                        zIndex: 0,
                      })}
                    />
                  ) : null}
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{
                      mt: 1,
                      fontWeight: stage.state === "current" ? 700 : 500,
                      color:
                        stage.state === "current"
                          ? "primary.main"
                          : "text.secondary",
                      lineHeight: 1.2,
                      px: 0.5,
                    }}
                  >
                    {stage.title}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Corridor
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {corridor.origin} → {corridor.destination}
            </Typography>
          </Box>
          {view.current ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Current
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {view.current.title}
              </Typography>
            </Box>
          ) : null}
          {view.next ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Next
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {view.next.title}
              </Typography>
            </Box>
          ) : null}
          {updatedLabel ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Updated
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {updatedLabel}
              </Typography>
            </Box>
          ) : null}
          {eta ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                ETA
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {(() => {
                  try {
                    return new Date(eta).toLocaleString();
                  } catch {
                    return String(eta);
                  }
                })()}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
